import React, { useEffect, useMemo, useRef } from "react";
import './calendarMobile.css'

const WEEK_DAYS = ["L", "M", "M", "J", "V", "S", "D"];

interface CalendarDayProps {
    date: Date;
    selected: boolean;
    today: boolean;
    locked: boolean;
    onSelect: (date: Date) => void;
    dayRef?: React.Ref<HTMLButtonElement>;
}

const CalendarDay = React.memo(({date,selected,today,locked,onSelect,dayRef}: CalendarDayProps) => {
    return (
        <button type="button" disabled={locked} ref={dayRef}
            className={["mobileCalendarDay XsmallText",selected ? "date_select" : "",today ? "current_Date" : "",locked ? "lockDay" : ""].filter(Boolean).join(" ")}
            onClick={() => onSelect(date)}
        >
            {date.getDate()}
        </button>
    );
});


const getMonthDays = (monthDate: Date) => {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const startOffset = (firstDay.getDay() + 6) % 7;
    const days: (Date | null)[] = [];

    for (let i = 0; i < startOffset; i++) days.push(null);
    for (let day = 1; day <= daysInMonth; day++) days.push(new Date(year, month, day));

    while (days.length % 7 !== 0) {
        days.push(null);
    }

    return days;
};

const isSameDay = (a: Date, b: Date) => a.getDate() === b.getDate() && a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear();

interface CalendarMobileProps {
    handleClickDay:(value:Date) => void,
    currentDate:Date|null,
}

const CalendarMobile:React.FC<CalendarMobileProps> = React.memo(({handleClickDay,currentDate}) => {
    const { today, maxDate } = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const maxDate = new Date(today.getFullYear(),today.getMonth() + 4,0);
        maxDate.setHours(23, 59, 59, 999);
        return { today, maxDate };
    }, []);
    const months = useMemo(() => {
        return Array.from({ length: 4 }, (_, index) => {
            return new Date(
                today.getFullYear(),
                today.getMonth() + index,
                1
            );
        });
    }, [today]);
    const monthsWithDays = useMemo(() => {
        return months.map((month) => {
            const monthLabel = month.toLocaleDateString("fr-FR", {month: "long",year: "numeric"});
            return {
                month,
                label: monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1),
                days: getMonthDays(month)
            };
        });
    }, [months]);

    const scrollRef = useRef<HTMLDivElement>(null);
    const selectedDayRef = useRef<HTMLButtonElement>(null);
    useEffect(() => {
        if (!currentDate) return;
        if (!scrollRef.current || !selectedDayRef.current) return;

        const container = scrollRef.current;
        const target = selectedDayRef.current;

        const containerRect = container.getBoundingClientRect();
        const targetRect = target.getBoundingClientRect();

        const targetTop = targetRect.top - containerRect.top + container.scrollTop;
        container.scrollTo({top: Math.max(0, targetTop - 60),behavior: "auto"});
    }, []);
    
    return <div className="containerCalendarMobile smallText">
        <div className="mobileCalendarWeekDays smallText">
            {WEEK_DAYS.map((day, index) => (
                <span key={index}>{day}</span>
            ))}
        </div>
        <div className="containerCorp">
            <div className="shadowTop"></div>
            <div className="shadowBottom"></div>
            <div className="mobileCalendarScroll" ref={scrollRef}>
                {monthsWithDays.map(({ month, label, days }) => {
                     return <div className="mobileCalendarMonth" key={`${month.getFullYear()}-${month.getMonth()}`} >
                            <div className="mobileCalendarMonthTitle smallTextBold">
                                {label}
                            </div>

                            <div className="mobileCalendarGrid">
                                {days.map((date, index) => {
                                    if (!date) {
                                        return <span key={`empty-${index}`} className="mobileCalendarEmpty"/>
                                    }

                                    const isSelected = currentDate !== null && isSameDay(date, currentDate);
                                    const isToday = isSameDay(date, today);
                                    const isLocked = date < today || date > maxDate;

                                    return (
                                        <CalendarDay
                                            key={date.getTime()}
                                            date={date}
                                            selected={isSelected}
                                            today={isToday}
                                            locked={isLocked}
                                            onSelect={handleClickDay}
                                            dayRef={isSelected ? selectedDayRef : undefined}
                                        />

                                    );
                                })}
                            </div>
                        </div>}
                )}
            </div>
        </div>
    </div>
})

export default CalendarMobile;